import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DEFAULT_PAGE_SIZE } from './api'

/**
 * One list view — its filters and its place in the results — held in the URL.
 *
 * Every list in this product had the same two problems, and solving them once
 * beats solving them five times slightly differently. The filters lived in
 * component state, so a view somebody had built could only be described to a
 * colleague in words; and the page was a cursor in memory, so a refresh threw
 * away where you were.
 *
 * What goes in the URL is chosen, not everything:
 *
 *   - the FILTERS, because they describe a view and are stable;
 *   - the page SIZE, for the same reason;
 *   - the page NUMBER, which is the one that needs an argument. It cannot be
 *     jumped to, because a cursor store pages forward from a key — so a link
 *     naming page four is restored by walking forward to it, one request per
 *     page. That is bounded by {@link MAX_RESTORED_PAGES}; past the bound the
 *     view opens at the start rather than making a browser fetch forty pages
 *     nobody asked to look at.
 *
 * What does NOT go in the URL is the cursor itself. It belongs to one query
 * against one snapshot of the data, so a link carrying one would open on rows
 * that had since moved — and unlike a stale page number, nothing about the
 * result would look wrong.
 *
 * The one invariant worth stating plainly: a page is reachable only once its
 * cursor has been recorded. Everything else here is convenience; this is what
 * stops the view showing one page of rows under four different page numbers.
 */

/** How far a shared link may ask the browser to walk forward when it opens. */
export const MAX_RESTORED_PAGES = 20

/** The size options every list offers. The first is the default. */
export const PAGE_SIZES = [25, 50, 100] as const

/**
 * Whether a page can be moved to from where we are.
 *
 * The whole rule, in one place, so it can be read and tested as the rule it is.
 *
 * Backwards is always allowed: those pages have been walked. Forwards is allowed
 * only to a page whose cursor has been RECORDED, and a cursor is recorded only
 * when the server sent one — so the page after a list that ended is not
 * reachable, and neither is anything past it.
 *
 * The failure this exists to stop is quiet. A page with no recorded cursor read
 * as `null`, `null` is the first page's cursor, and the grid re-fetched page one
 * and showed it as page four. Seven contacts became four pages of the same
 * seven, each with a live next arrow and a footer counting rows that were never
 * there.
 */
export function canReachPage(from: number, to: number, cursors: ReadonlyArray<string | null>): boolean {
  if (to < 0) return false
  if (to <= from) return true
  return cursors[to] !== undefined
}

/**
 * The page index a link is asking to be restored to, or the start.
 *
 * Parsed strictly rather than with `Number`, because a link that has been
 * truncated or retyped by hand is the normal way this arrives. `?page=abc`
 * became NaN, and every comparison against NaN is false — so the walk below had
 * no target to stop at, stepped through the WHOLE list one request at a time
 * with nothing on screen to say so, and left the reader on the last page.
 *
 * Anything past the bound opens at the start instead, which is the promise made
 * at the top of this file: twenty requests to honour a link is already generous,
 * and forty is a browser fetching pages nobody asked to look at.
 */
export function restoreTarget(params: URLSearchParams): number {
  const raw = params.get('page') ?? ''
  // The WHOLE string, not a prefix of it. `parseInt` reads "2x" as 2, and a page
  // number is one of the few things where a partly-understood value is worse
  // than an ignored one: it opens a list somewhere nobody named.
  if (!/^\d+$/.test(raw)) return 0
  const asked = Number(raw)
  if (!Number.isSafeInteger(asked) || asked <= 1) return 0
  return asked - 1 > MAX_RESTORED_PAGES ? 0 : asked - 1
}

/**
 * Whether a list query has actually answered for the page on screen.
 *
 * Every list here keeps the previous page visible while the next one loads, so
 * `data` is very often the PREVIOUS request's data — React Query flags that as
 * `isPlaceholderData`, and treating it as an answer is how a page's cursor gets
 * attributed to the page after it.
 *
 * An error counts as answered. It is not the rows, but it is the end of waiting,
 * and a walk that never settles leaves the grid spinning with the error hidden
 * underneath it.
 */
export function listReady(query: {
  isFetching: boolean
  isPlaceholderData: boolean
  isError: boolean
  data: unknown
}): boolean {
  if (query.isError) return true
  return !query.isFetching && !query.isPlaceholderData && query.data !== undefined
}

/**
 * Where the cursor that reaches the NEXT page gets recorded, and when it must not be.
 *
 * Returns the new cursor list, or null to leave it alone. Pulled out of the
 * effect it used to live in because it is a rule, not a side effect, and this is
 * the rule the whole pager rests on.
 *
 * The third case is the one that was wrong, and it made "next" do nothing.
 *
 * Clicking next moves `page` immediately, but the data for the new page has not
 * arrived — so for a render or two the hook holds the NEW page number beside the
 * OLD page's cursor. Recording that pair writes page N's own cursor into slot
 * N+1, so "next" from page N fetches page N again. And because a slot is only
 * ever filled once, the wrong value is permanent: the reader is stuck, with a
 * live next arrow, on a page that will not advance. It only sometimes happened,
 * because React sometimes batches the click and the fetch into one commit and
 * the bad pair never renders — which is exactly what "it works on the third
 * try" looks like.
 *
 * The guard is an invariant rather than a timing fix: a server can never hand
 * back the cursor it was given, because that would be a page that contains
 * itself. So a `nextCursor` equal to this page's own cursor is proof the
 * response belongs to an earlier request, whatever the clock says.
 */
export function recordCursor(
  cursors: ReadonlyArray<string | null>,
  page: number,
  nextCursor: string | null,
): Array<string | null> | null {
  // A list that ended has no cursor to record, and no page after it.
  if (nextCursor === null) return null
  // Already walked. A slot is written once, so re-writing it could only replace
  // a known-good cursor with a newer guess.
  if (cursors.length > page + 1) return null
  // The response belongs to an older request than the page now on screen.
  if (nextCursor === cursors[page]) return null
  /*
   * A GAP is not something to fill in.
   *
   * This used to pad the missing slots with `null` and carry on — and `null` is
   * the FIRST page's cursor, so every padded slot was an instruction to fetch
   * page one again. That is what the reported fault actually was: pages
   * alternating between the first page and the second, for ever, because the
   * odd slots held null and the even ones held a real cursor.
   *
   * There is no honest value to put here. If the cursor for page N was never
   * recorded then nothing knows where page N starts, and a page whose start is
   * unknown is one the reader must not be sent to. Recording nothing leaves
   * `canReachPage` to keep the arrow dead, which is the truth.
   */
  if (cursors.length <= page) return null

  return [...cursors, nextCursor]
}

export interface ListPaging {
  page: number
  pageSize: number
  hasNextPage: boolean
  /**
   * True while walking forward to a page a link asked for.
   *
   * It travels with the paging rather than on its own, because the pager is what
   * has to act on it: the screens hand this object to the grid whole, so the
   * grid can hold its arrows still and say what it is doing without every screen
   * remembering to pass one more prop.
   */
  restoring: boolean
  onChange: (next: { page: number; pageSize: number }) => void
}

export interface ListView<F> {
  filters: F
  /** Applies filters and returns to the first page, which a filter change means. */
  applyFilters: (next: F) => void
  page: number
  pageSize: number
  /** The cursor for the page being shown. Null on the first page. */
  cursor: string | null
  /** Everything the grid's footer needs, including the restore walk. */
  paging: ListPaging
  /** How many filters are set, so a Clear button can say what it will clear. */
  activeFilterCount: number
}

export function useListView<F extends Record<string, string>>(opts: {
  /** Every filter with its empty value. Also defines the field order. */
  empty: F
  /** Filter field to query-string key. One mapping, used in both directions. */
  keys: Record<keyof F, string>
  /** Fields lowercased on the way in, like a stored domain. */
  lowercase?: ReadonlyArray<keyof F>
  /**
   * The cursor the server returned for the page on screen, and whether that
   * answer has arrived. Passing these in is what lets the hook own the walk
   * forward: each page's cursor is what makes the next one reachable, so it can
   * only ever advance one step per response.
   */
  nextCursor: string | null
  ready: boolean
}): ListView<F> {
  const { empty, keys, nextCursor, ready } = opts
  const lowercase = useMemo(() => new Set(opts.lowercase ?? []), [opts.lowercase])
  const [searchParams, setSearchParams] = useSearchParams()

  /*
   * Absent means "the default"; present-but-empty means "cleared on purpose".
   *
   * The distinction only matters once a filter has a non-empty default — the
   * Contacts screen opens on accepted contacts, because a list that leads with
   * rejected people is a list nobody can act on. Without it, choosing "Any"
   * wrote nothing to the URL, the next read saw nothing and applied the
   * default, and the control snapped back to Accepted on its own.
   */
  const readFilters = useCallback(
    (params: URLSearchParams): F => {
      const next = { ...empty }
      for (const field of Object.keys(empty) as Array<keyof F>) {
        const value = params.get(keys[field])
        if (value === null) continue
        next[field] = (lowercase.has(field) ? value.toLowerCase() : value) as F[keyof F]
      }
      return next
    },
    [empty, keys, lowercase],
  )

  /*
   * The latest `readFilters`, reachable without depending on its identity.
   *
   * The effect below must not re-run just because a caller passed a fresh array
   * literal; see the note there for what that cost.
   */
  const readFiltersRef = useRef(readFilters)
  readFiltersRef.current = readFilters

  const [filters, setFilters] = useState<F>(() => readFilters(searchParams))
  const [pageSize, setPageSize] = useState(() => {
    const asked = Number(searchParams.get('size'))
    return (PAGE_SIZES as readonly number[]).includes(asked) ? asked : DEFAULT_PAGE_SIZE
  })
  const [cursors, setCursors] = useState<Array<string | null>>([null])
  const [page, setPage] = useState(0)

  /*
   * The page a link asked for, which we are probably not on yet.
   *
   * A ref rather than state: reaching it is a side effect of pages arriving, and
   * re-rendering on every step would be a second source of truth for where we
   * are going.
   */
  const target = useRef(restoreTarget(searchParams))

  /*
   * The query string this hook last wrote, so it can tell its own writes from
   * somebody else's. Seeded with what we opened on, which we are already showing.
   */
  const written = useRef(searchParams.toString())

  /*
   * Put the view back in the address bar, keeping whatever else is in there.
   *
   * This hook owns SOME of the query string — the filters it was handed, `page`
   * and `size` — and must not act as though it owns all of it. Building a fresh
   * URLSearchParams here deleted every key it did not recognise on the next
   * filter or page change, which is why the job-detail tabs could not adopt this
   * hook: they carry `?tab=contacts`, and the first step to page two would have
   * dropped the reader back on the Companies tab halfway down a list.
   *
   * So each of its own keys is set or deleted by name, and nothing else is
   * touched. Deleting rather than skipping still matters: a filter cleared back
   * to empty has to leave the URL, or the link says it is set.
   */
  const write = useCallback(
    (next: { filters: F; page: number; pageSize: number }) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current)
          for (const field of Object.keys(empty) as Array<keyof F>) {
            /*
             * Only what DIFFERS from the default. A filter sitting at its
             * default is noise in a link somebody is about to paste into a
             * message — and a filter cleared away from a non-empty default is
             * written as an empty value rather than dropped, because dropping
             * it would read back as the default and undo the choice.
             */
            if (next.filters[field] === empty[field]) params.delete(keys[field])
            else params.set(keys[field], next.filters[field] ?? '')
          }
          if (next.pageSize !== DEFAULT_PAGE_SIZE) params.set('size', String(next.pageSize))
          else params.delete('size')
          if (next.page > 0) params.set('page', String(next.page + 1))
          else params.delete('page')
          written.current = params.toString()
          return params
        },
        /*
         * Replace, not push. Typing in a search box changes the filters on every
         * keystroke, and pushing would bury whatever the person was looking at
         * before under thirty history entries they have to press Back through.
         */
        { replace: true },
      )
    },
    [empty, keys, setSearchParams],
  )

  const goTo = useCallback(
    (nextPage: number, nextSize: number) => {
      setPage(nextPage)
      setPageSize(nextSize)
      write({ filters, page: nextPage, pageSize: nextSize })
    },
    [filters, write],
  )

  const restart = useCallback(
    (next: F, nextSize: number) => {
      setFilters(next)
      setCursors([null])
      setPage(0)
      setPageSize(nextSize)
      target.current = 0
      write({ filters: next, page: 0, pageSize: nextSize })
    },
    [write],
  )

  const applyFilters = useCallback(
    (next: F) => {
      // A cursor belongs to the query that produced it, so a filter change pages
      // from the beginning rather than into the wrong result.
      restart(next, pageSize)
    },
    [pageSize, restart],
  )

  /*
   * Follow the address bar when something outside this hook changes it.
   *
   * The filters were read once, at mount, and never read again. Clicking
   * "Contacts" in the nav bar while already on /contacts?q=acme replaces the
   * location without unmounting the route, so the URL said one thing and the
   * grid went on showing the filtered rows — with the Clear filters button still
   * lit, and Copy link handing a colleague a link to a list nobody was looking
   * at. Starting again is the same rule a filter change follows: a cursor
   * belongs to the query that produced it.
   *
   * This hook's own writes come back through here too, and are recognised by the
   * query string they wrote rather than by a flag that could be left set.
   *
   * It depends on `searchParams` AND NOTHING ELSE, which is load-bearing.
   *
   * `written` records what this hook is about to put in the address bar, and the
   * router commits that a render or two later. So there is a window in which
   * `written` is already the NEW query string while `searchParams` is still the
   * old one — and any run of this effect inside that window compares them,
   * finds them different, and concludes that somebody outside changed the URL.
   * It then throws away the cursors and sends the reader back to page one.
   *
   * With `readFilters` in the dependency list that window was reachable, because
   * `readFilters` is only as stable as the options handed to the hook: the audit
   * screen passes `lowercase: ['actor']` as a literal, so a new array per render
   * made a new `readFilters`, which ran this effect on every render. Paging then
   * broke on that screen alone, in a way that looked like the pager and was not.
   *
   * Depending only on `searchParams` means this can only run when the address
   * bar has ACTUALLY changed — at which point `written` is either equal to it
   * (our write, landed) or genuinely stale (somebody else's). The read of
   * `readFilters` goes through a ref so that using the latest one costs nothing.
   */
  useEffect(() => {
    const live = searchParams.toString()
    if (live === written.current) return
    written.current = live
    const asked = Number(searchParams.get('size'))
    setFilters(readFiltersRef.current(searchParams))
    setPageSize((PAGE_SIZES as readonly number[]).includes(asked) ? asked : DEFAULT_PAGE_SIZE)
    setCursors([null])
    setPage(0)
    target.current = restoreTarget(searchParams)
  }, [searchParams])

  /*
   * Record the cursor that reaches the page after this one.
   *
   * Written on arrival rather than on click, so Back is free and so the walk
   * forward below has something to step on.
   */
  useEffect(() => {
    if (!ready) return
    setCursors((prev) => recordCursor(prev, page, nextCursor) ?? prev)
  }, [ready, nextCursor, page])

  /*
   * Walk forward to the page a link asked for.
   *
   * One step per response, because each page's cursor is what makes the next
   * reachable. It stops on its own when the target is reached, and also when the
   * data runs out — a link to page four of a list that now has two pages opens
   * on page two rather than spinning.
   */
  useEffect(() => {
    if (!ready || page >= target.current) return
    if (nextCursor === null) {
      target.current = page
      write({ filters, page, pageSize })
      return
    }
    setPage(page + 1)
  }, [ready, nextCursor, page, filters, pageSize, write])

  /*
   * A link asking for a page we will not walk to opens at the start, and the URL
   * is corrected to say so.
   *
   * Leaving `?page=41` in the address bar above page one is the same lie the
   * walk exists to avoid, pointing the other way: the next person to copy that
   * link would pass on a page nobody had been looking at. Once, on open — after
   * that the address bar is written by this hook and always agrees.
   */
  const corrected = useRef(false)
  useEffect(() => {
    if (corrected.current) return
    corrected.current = true
    if (target.current === 0 && searchParams.has('page')) write({ filters, page: 0, pageSize })
  }, [searchParams, filters, pageSize, write])

  const paging: ListPaging = {
    page,
    pageSize,
    hasNextPage: nextCursor !== null,
    restoring: page < target.current,
    onChange: ({ page: nextPage, pageSize: nextSize }) => {
      if (nextSize !== pageSize) {
        /*
         * A cursor belongs to the page size that produced it. Resuming a 25-row
         * cursor at 100 would silently skip rows, with nothing anywhere to say
         * so — a size change starts again.
         */
        restart(filters, nextSize)
        return
      }
      /*
       * Forward only as far as there is somewhere to go.
       *
       * A page is reachable when its cursor has been recorded, and a cursor is
       * only recorded when the server sent one — so a list that ended has no
       * page after it and this refuses to pretend otherwise.
       *
       * Without the guard the read below fell back to `null` for a page nobody
       * had walked to, `null` is the FIRST page's cursor, and the grid re-fetched
       * page one and displayed it as page four. Seven contacts became four pages
       * of the same seven, each with a next arrow, and nothing anywhere said the
       * numbers on the footer were fiction.
       *
       * Belt and braces with the disabled arrow rather than instead of it: this
       * is the half that cannot be undone by a component library changing its
       * mind about what an unknown total means.
       */
      if (!canReachPage(page, nextPage, cursors)) return
      goTo(nextPage, pageSize)
    },
  }

  /*
   * Counted against the DEFAULTS, not against the empty string.
   *
   * A screen whose default is "accepted" would otherwise open reporting one
   * filter set, with a Clear button lit for a view nobody had narrowed — and
   * clearing it would move the list rather than restore it.
   */
  const activeFilterCount = (Object.keys(empty) as Array<keyof F>).filter(
    (field) => filters[field] !== empty[field],
  ).length

  return {
    filters,
    applyFilters,
    page,
    pageSize,
    cursor: cursors[page] ?? null,
    paging,
    activeFilterCount,
  }
}
